import React from 'react';

export type StatusBadgeVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'purple'
  | 'primary';

interface StatusBadgeProps {
  /** Fixed palette variant — maps to the .ds-badge-{variant} classes in design-system.css */
  variant?: StatusBadgeVariant;
  /** Arbitrary accent color (e.g. event type / category color from user data).
   *  Mutually exclusive with `variant` — takes the .ds-badge-dynamic path. */
  color?: string;
  /** When provided, renders as a clickable badge-button (.ds-status-cycle) instead of a static span. */
  onClick?: () => void;
  title?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Thin wrapper around the existing `.ds-badge` design-system CSS
 * (see src/styles/design-system.css ~L916-960). Does not introduce
 * new visual styles — just centralizes the className string-building
 * that was previously duplicated inline across components.
 */
export default function StatusBadge({
  variant,
  color,
  onClick,
  title,
  className = '',
  children,
}: StatusBadgeProps) {
  const variantClass = color ? 'ds-badge-dynamic' : `ds-badge-${variant ?? 'neutral'}`;
  const classes = [
    'ds-badge',
    onClick ? 'ds-status-cycle' : '',
    variantClass,
    className,
  ].filter(Boolean).join(' ');

  const style = color ? ({ '--ds-badge-accent': color } as React.CSSProperties) : undefined;

  if (onClick) {
    return (
      <button type="button" className={classes} style={style} onClick={onClick} title={title}>
        {children}
      </button>
    );
  }

  return (
    <span className={classes} style={style} title={title}>
      {children}
    </span>
  );
}
